import React from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import FooterEnhanced from '../components/FooterEnhanced';
import AIReadinessAssessment from '../components/AIReadinessAssessment';
import { SparklesIcon } from '@heroicons/react/24/outline';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #0B98CE 0%, #316EA8 100%);
  padding: 120px 20px 60px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(11, 216, 162, 0.2) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(11, 152, 206, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;

  svg {
    width: 40px;
    height: 40px;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  line-height: 1.6;
  margin: 0 0 10px 0;
`;

const HeroDescription = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 40px 20px 80px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const IntroTitle = styled.h2`
  color: #2d3748;
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0 0 20px 0;
`;

const IntroText = styled.p`
  color: #4a5568;
  font-size: 1.1rem;
  line-height: 1.7;
  margin: 0 0 15px 0;
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin: 40px 0;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`;

const BenefitCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const BenefitIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0BD8A2 0%, #0B98CE 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  color: white;
  font-size: 24px;
`;

const BenefitTitle = styled.h3`
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const BenefitText = styled.p`
  color: #718096;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
`;

const AssessmentSection = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 40px;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Assessment: React.FC = () => {
  return (
    <PageContainer>
      <Header />
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <SparklesIcon />
            AI Readiness Assessment
          </HeroTitle>
          <HeroSubtitle>
            Discover Your Organization's AI Potential in 5 Minutes
          </HeroSubtitle>
          <HeroDescription>
            Get a personalized report with actionable insights and quick wins
          </HeroDescription>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <IntroSection>
          <IntroTitle>Is Your Business Ready for AI?</IntroTitle>
          <IntroText>
            This assessment evaluates your current capabilities across key dimensions 
            and provides a customized roadmap for AI implementation.
          </IntroText>
        </IntroSection>


        <AssessmentSection>
          <AIReadinessAssessment />
        </AssessmentSection>
      </MainContent>

      <FooterEnhanced />
    </PageContainer>
  );
};

export default Assessment;